import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  Upload,
  X,
  Star,
  Package
} from 'lucide-react';
import api, { getImageUrl } from '../../utils/api';
import { Product } from '../../types';

interface ProductFormData {
  name: string;
  description: string;
  price: string;
  originalPrice: string;
  discountedPrice: string;
  category: string;
  brand: string;
  stock: string;
  featured: boolean;
  details: {
    sizes: string[];
    colors: string[];
    material: string;
    specifications: { key: string; value: string }[];
  };
}

const ProductManagement: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [imagePreview, setImagePreview] = useState<string[]>([]);
  const [existingImages, setExistingImages] = useState<string[]>([]);
  const [submitMessage, setSubmitMessage] = useState<string>('');
  const [submitError, setSubmitError] = useState<string>('');
  const [isDeletingAllProducts, setIsDeletingAllProducts] = useState(false);
  const [formData, setFormData] = useState<ProductFormData>({
    name: '',
    description: '',
    price: '',
    originalPrice: '',
    discountedPrice: '',
    category: '',
    brand: '',
    stock: '',
    featured: false,
    details: {
      sizes: [],
      colors: [],
      material: '',
      specifications: []
    }
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await api.get('/products?limit=100');
      setProducts(response.data.products);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setSelectedImages(files);

    // Create preview URLs
    const previews = files.map(file => URL.createObjectURL(file));
    setImagePreview(previews);
  };

  const removeImage = (index: number) => {
    const newImages = selectedImages.filter((_, i) => i !== index);
    const newPreviews = imagePreview.filter((_, i) => i !== index);
    setSelectedImages(newImages);
    setImagePreview(newPreviews);
  };

  const removeExistingImage = async (index: number) => {
    if (!editingProduct) return;
    
    const imageToDelete = existingImages[index];
    if (!window.confirm(`Delete this image: ${imageToDelete}?`)) {
      return;
    }

    try {
      await api.delete(`/products/${editingProduct._id}/images/${imageToDelete}`);
      
      // Update local state
      const newExistingImages = existingImages.filter((_, i) => i !== index);
      setExistingImages(newExistingImages);
      
      alert('Image deleted successfully!');
    } catch (error) {
      console.error('Error deleting image:', error);
      alert('Error deleting image. Please try again.');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitMessage('');
    setSubmitError('');
    
    // Validation
    if (!editingProduct && selectedImages.length === 0) {
      setSubmitError('At least one image is required for new products');
      return;
    }
    
    const submitFormData = new FormData();
    submitFormData.append('name', formData.name);
    submitFormData.append('description', formData.description);
    submitFormData.append('price', formData.price);
    if (formData.originalPrice && formData.originalPrice.trim() !== '') {
      submitFormData.append('originalPrice', formData.originalPrice);
    }
    if (formData.discountedPrice && formData.discountedPrice.trim() !== '') {
      submitFormData.append('discountedPrice', formData.discountedPrice);
    }
    submitFormData.append('category', formData.category);
    submitFormData.append('brand', formData.brand);
    submitFormData.append('stock', formData.stock);
    submitFormData.append('featured', formData.featured.toString());
    submitFormData.append('details', JSON.stringify(formData.details));

    // Add existing images data for edit mode
    if (editingProduct) {
      submitFormData.append('existingImages', JSON.stringify(existingImages));
    }

    // Add new images if any
    selectedImages.forEach(image => {
      submitFormData.append('images', image);
    });

    try {
      if (editingProduct) {
        await api.put(`/products/${editingProduct._id}`, submitFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setSubmitMessage('Product updated successfully!');
      } else {
        await api.post('/products', submitFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setSubmitMessage('Product created successfully!');
      }
      
      setTimeout(() => {
        resetForm();
        fetchProducts();
      }, 1500);
    } catch (error: any) {
      console.error('Error saving product:', error);
      const errorMessage = error.response?.data?.message || 'Error saving product. Please try again.';
      setSubmitError(errorMessage);
    }
  };

  const handleDelete = async (productId: string) => {
    const product = products.find(p => p._id === productId);
    const productName = product?.name || 'this product';
    
    const deleteType = window.confirm(
      `Choose deletion type for "${productName}":\n\n` +
      `• OK/Yes = HARD DELETE (Permanently remove product AND all its images)\n` +
      `• Cancel/No = SOFT DELETE (Hide product but keep images)\n\n` +
      `Hard delete cannot be undone!`
    );

    if (deleteType) {
      // Hard delete - remove product and images
      if (!window.confirm(
        `⚠️ FINAL CONFIRMATION ⚠️\n\n` +
        `You are about to PERMANENTLY DELETE:\n` +
        `• Product: "${productName}"\n` +
        `• All product images (${product?.images?.length || 0} images)\n\n` +
        `This action CANNOT be undone. Continue?`
      )) {
        return;
      }
      
      try {
        const response = await api.delete(`/products/${productId}?hard=true`);
        const { deletedProduct, deletedImagesCount } = response.data;
        alert(
          `✅ PERMANENTLY DELETED:\n\n` +
          `Product: "${deletedProduct}"\n` +
          `Images: ${deletedImagesCount} files removed from server\n\n` +
          `This action was irreversible.`
        );
        fetchProducts();
      } catch (error) {
        console.error('Error deleting product:', error);
        alert('Error deleting product. Please try again.');
      }
    } else {
      // Soft delete - just hide product
      if (window.confirm(`Hide "${productName}" from store? (Images will be preserved)`)) {
        try {
          const response = await api.delete(`/products/${productId}`);
          const { hiddenProduct, preservedImagesCount } = response.data;
          alert(
            `✅ HIDDEN FROM STORE:\n\n` +
            `Product: "${hiddenProduct}"\n` +
            `Images: ${preservedImagesCount} files preserved on server\n\n` +
            `Product can be reactivated later if needed.`
          );
          fetchProducts();
        } catch (error) {
          console.error('Error hiding product:', error);
          alert('Error hiding product. Please try again.');
        }
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      price: '',
      originalPrice: '',
      discountedPrice: '',
      category: '',
      brand: '',
      stock: '',
      featured: false,
      details: {
        sizes: [],
        colors: [],
        material: '',
        specifications: []
      }
    });
    setSelectedImages([]);
    setImagePreview([]);
    setExistingImages([]);
    setSubmitMessage('');
    setSubmitError('');
    setShowAddForm(false);
    setEditingProduct(null);
  };

  // Delete all products function
  const deleteAllProducts = async () => {
    if (products.length === 0) {
      alert('No products to delete.');
      return;
    }

    const confirmMessage = `⚠️ DANGER: DELETE ALL PRODUCTS ⚠️\n\n` +
      `You are about to PERMANENTLY DELETE:\n` +
      `• ALL ${products.length} products from your store\n` +
      `• ALL product images and data\n\n` +
      `This action CANNOT be undone!\n\n` +
      `Are you absolutely sure?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    // Final confirmation
    if (!window.confirm(`FINAL CONFIRMATION:\n\nDelete ALL ${products.length} products permanently?`)) {
      return;
    }

    try {
      setIsDeletingAllProducts(true);
      const response = await api.delete('/products/all');
      
      const { 
        deletedProductsCount, 
        deletedImagesCount 
      } = response.data;
      
      alert(
        `✅ SUCCESSFULLY DELETED:\n\n` +
        `Products: ${deletedProductsCount} removed\n` +
        `Images: ${deletedImagesCount} files deleted\n\n` +
        `Your store is now empty.`
      );
      
      // Refresh the products list
      fetchProducts();
    } catch (error: any) {
      console.error('Error deleting all products:', error);
      const errorMessage = error.response?.data?.message || 'Failed to delete all products';
      alert(`Error: ${errorMessage}`);
    } finally {
      setIsDeletingAllProducts(false);
    }
  };




  const startEdit = (product: Product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      originalPrice: product.originalPrice?.toString() || '',
      discountedPrice: product.discountedPrice?.toString() || '',
      category: product.category,
      brand: product.brand || '',
      stock: product.stock.toString(),
      featured: product.featured,
      details: {
        sizes: product.details?.sizes || [],
        colors: product.details?.colors || [],
        material: product.details?.material || '',
        specifications: product.details?.specifications || []
      }
    });
    
    // Set existing images and clear new selections
    setExistingImages(product.images || []);
    setSelectedImages([]);
    setImagePreview([]);
    setSubmitMessage('');
    setSubmitError('');
    setShowAddForm(true);
  };

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 dark:border-primary-400 mx-auto mb-4"></div>
        <p className="text-gray-600 dark:text-gray-400">Loading products...</p>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-6 space-y-4 sm:space-y-0">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">
            Product Management
          </h2>
          <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base">
            Manage your product catalog
          </p>
        </div>
        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
          <button
            onClick={deleteAllProducts}
            disabled={isDeletingAllProducts}
            className="bg-red-600 hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600 
                     text-white px-3 sm:px-4 py-2 rounded-lg disabled:bg-gray-400 dark:disabled:bg-gray-600 
                     flex items-center justify-center space-x-2 transition-colors text-sm sm:text-base"
          >
            <Trash2 className="h-4 w-4" />
            <span>{isDeletingAllProducts ? 'Deleting...' : 'Delete All Products'}</span>
          </button>
          <button
            onClick={() => setShowAddForm(true)}
            className="bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 
                     text-white px-3 sm:px-4 py-2 rounded-lg flex items-center justify-center space-x-2 
                     transition-colors text-sm sm:text-base"
          >
            <Plus className="h-4 w-4" />
            <span>Add Product</span>
          </button>
        </div>
      </div>

      {/* Add/Edit Form Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-start sm:items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white dark:bg-secondary-800 rounded-lg max-w-4xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto my-4 sm:my-0">
            <form onSubmit={handleSubmit} className="p-4 sm:p-6">
              <div className="flex justify-between items-center mb-4 sm:mb-6">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-100">
                  {editingProduct ? 'Edit Product' : 'Add New Product'}
                </h3>
                <button
                  type="button"
                  onClick={resetForm}
                  className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 
                           p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-secondary-700 transition-colors"
                >
                  <X className="h-5 w-5 sm:h-6 sm:w-6" />
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                {/* Basic Information */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Product Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-secondary-600 
                               bg-white dark:bg-secondary-700 text-gray-900 dark:text-gray-100
                               rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 
                               focus:border-transparent transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Description *
                    </label>
                    <textarea
                      required
                      rows={4}
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-secondary-600 
                               bg-white dark:bg-secondary-700 text-gray-900 dark:text-gray-100
                               rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 
                               focus:border-transparent transition-colors resize-vertical"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Price *
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        required
                        value={formData.price}
                        onChange={(e) => setFormData({...formData, price: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-secondary-600 
                                 bg-white dark:bg-secondary-700 text-gray-900 dark:text-gray-100
                                 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 
                                 focus:border-transparent transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Stock *
                      </label>
                      <input
                        type="number"
                        required
                        value={formData.stock}
                        onChange={(e) => setFormData({...formData, stock: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-secondary-600 
                                 bg-white dark:bg-secondary-700 text-gray-900 dark:text-gray-100
                                 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 
                                 focus:border-transparent transition-colors"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Original Price (for discount display)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.originalPrice}
                        onChange={(e) => setFormData({...formData, originalPrice: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-secondary-600 
                                 bg-white dark:bg-secondary-700 text-gray-900 dark:text-gray-100
                                 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 
                                 focus:border-transparent transition-colors"
                        placeholder="e.g., 1500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Discounted Price
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.discountedPrice}
                        onChange={(e) => setFormData({...formData, discountedPrice: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-secondary-600 
                                 bg-white dark:bg-secondary-700 text-gray-900 dark:text-gray-100
                                 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 
                                 focus:border-transparent transition-colors"
                        placeholder="e.g., 1200"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Category *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.category}
                        onChange={(e) => setFormData({...formData, category: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-secondary-600 
                                 bg-white dark:bg-secondary-700 text-gray-900 dark:text-gray-100
                                 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 
                                 focus:border-transparent transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Brand
                      </label>
                      <input
                        type="text"
                        value={formData.brand}
                        onChange={(e) => setFormData({...formData, brand: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-secondary-600 
                                 bg-white dark:bg-secondary-700 text-gray-900 dark:text-gray-100
                                 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 
                                 focus:border-transparent transition-colors"
                      />
                    </div>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="featured"
                      checked={formData.featured}
                      onChange={(e) => setFormData({...formData, featured: e.target.checked})}
                      className="mr-2 w-4 h-4 text-primary-600 bg-white dark:bg-secondary-700 
                               border-gray-300 dark:border-secondary-600 rounded 
                               focus:ring-primary-500 dark:focus:ring-primary-400"
                    />
                    <label htmlFor="featured" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Featured Product
                    </label>
                  </div>
                </div>

                {/* Image Upload */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Product Images *
                    </label>
                    <div className="border-2 border-dashed border-gray-300 dark:border-secondary-600 
                                  rounded-lg p-4 sm:p-6 text-center bg-gray-50 dark:bg-secondary-700/50">
                      <Upload className="h-8 w-8 sm:h-12 sm:w-12 text-gray-400 dark:text-gray-500 mx-auto mb-3 sm:mb-4" />
                      <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-2">
                        Click to upload or drag and drop
                      </p>
                      <input
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={handleImageSelect}
                        className="hidden"
                        id="image-upload"
                      />
                      <label
                        htmlFor="image-upload"
                        className="bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 
                                 text-white px-3 sm:px-4 py-2 rounded-md cursor-pointer transition-colors 
                                 text-xs sm:text-sm font-medium"
                      >
                        Select Images
                      </label>
                    </div>

                    {/* Existing Images (for edit mode) */}
                    {existingImages.length > 0 && (
                      <div className="mt-4">
                        <p className="text-sm font-medium text-gray-700 mb-2">Current Images:</p>
                        <div className="grid grid-cols-3 gap-2">
                          {existingImages.map((image, index) => (
                            <div key={`existing-${index}`} className="relative">
                              <img
                                src={getImageUrl(image)}
                                alt={`Current ${index + 1}`}
                                className="w-full h-20 object-cover rounded-md"
                              />
                              <button
                                type="button"
                                onClick={() => removeExistingImage(index)}
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 disabled:bg-gray-400"
                                title="Delete this image"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* New Image Preview */}
                    {imagePreview.length > 0 && (
                      <div className="mt-4">
                        <p className="text-sm font-medium text-gray-700 mb-2">New Images:</p>
                        <div className="grid grid-cols-3 gap-2">
                          {imagePreview.map((preview, index) => (
                            <div key={`new-${index}`} className="relative">
                              <img
                                src={preview}
                                alt={`Preview ${index + 1}`}
                                className="w-full h-20 object-cover rounded-md"
                              />
                              <button
                                type="button"
                                onClick={() => removeImage(index)}
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Success/Error Messages */}
              {submitMessage && (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 
                              text-green-700 dark:text-green-400 px-4 py-3 rounded-md mt-4">
                  {submitMessage}
                </div>
              )}
              {submitError && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 
                              text-red-700 dark:text-red-400 px-4 py-3 rounded-md mt-4">
                  {submitError}
                </div>
              )}

              {/* Submit Buttons */}
              <div className="flex flex-col sm:flex-row sm:justify-end space-y-3 sm:space-y-0 sm:space-x-3 mt-6 pt-6 
                            border-t border-gray-200 dark:border-secondary-600">
                <button
                  type="button"
                  onClick={resetForm}
                  className="w-full sm:w-auto px-4 py-2 text-gray-700 dark:text-gray-300 
                           bg-gray-100 dark:bg-secondary-700 rounded-md hover:bg-gray-200 
                           dark:hover:bg-secondary-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="w-full sm:w-auto px-4 py-2 bg-primary-600 hover:bg-primary-700 
                           dark:bg-primary-500 dark:hover:bg-primary-600 text-white rounded-md 
                           disabled:bg-gray-400 dark:disabled:bg-gray-600 disabled:cursor-not-allowed 
                           transition-colors"
                  disabled={!editingProduct && selectedImages.length === 0}
                >
                  {editingProduct ? 'Update Product' : 'Add Product'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Products Table */}
      <div className="bg-white dark:bg-secondary-800 rounded-lg border border-gray-200 dark:border-secondary-700 shadow-sm">
        {/* Mobile Card View */}
        <div className="block sm:hidden">
          <div className="divide-y divide-gray-200 dark:divide-secondary-700">
            {products.map((product) => (
              <div key={product._id} className="p-4 bg-white dark:bg-secondary-800 hover:bg-gray-50 dark:hover:bg-secondary-750 transition-colors">
                {/* Product Header with Image and Actions */}
                <div className="flex items-start space-x-3 mb-3">
                  <div className="h-16 w-16 flex-shrink-0">
                    {product.images.length > 0 ? (
                      <img
                        src={getImageUrl(product.images[0])}
                        alt={product.name}
                        className="h-16 w-16 rounded-lg object-cover border border-gray-200 dark:border-secondary-600"
                      />
                    ) : (
                      <div className="h-16 w-16 rounded-lg bg-gray-100 dark:bg-secondary-700 flex items-center justify-center border border-gray-200 dark:border-secondary-600">
                        <Package className="h-8 w-8 text-gray-400 dark:text-gray-500" />
                      </div>
                    )}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    {/* Product Name - Allow wrapping for long names */}
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 leading-5 mb-1 break-words">
                      {product.name}
                    </h3>
                    
                    {/* Category and Brand */}
                    <div className="flex flex-wrap items-center gap-1 mb-2">
                      <span className="text-xs bg-gray-100 dark:bg-secondary-700 text-gray-700 dark:text-gray-300 px-2 py-1 rounded-full">
                        {product.category}
                      </span>
                      {product.brand && (
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          • {product.brand}
                        </span>
                      )}
                    </div>
                    
                    {/* Status and Featured Badge */}
                    <div className="flex items-center space-x-2 mb-2">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        product.active 
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' 
                          : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                      }`}>
                        {product.active ? 'Active' : 'Inactive'}
                      </span>
                      {product.featured && (
                        <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 rounded-full">
                          <Star className="h-3 w-3 mr-1 fill-current" />
                          Featured
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex flex-col space-y-2">
                    <button
                      onClick={() => startEdit(product)}
                      className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-200 
                               hover:bg-indigo-50 dark:hover:bg-indigo-900/20 p-2 rounded-lg transition-colors"
                      title="Edit Product"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(product._id)}
                      className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 
                               hover:bg-red-50 dark:hover:bg-red-900/20 p-2 rounded-lg transition-colors"
                      title="Delete Product"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                
                {/* Product Details */}
                <div className="border-t border-gray-100 dark:border-secondary-700 pt-3">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center space-x-4">
                      <div className="text-center">
                        <div className="text-lg font-bold text-gray-900 dark:text-gray-100">
                          ₹{product.price.toLocaleString()}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Price</div>
                      </div>
                      <div className="text-center">
                        <div className={`text-sm font-semibold ${
                          product.stock > 10 
                            ? 'text-green-600 dark:text-green-400' 
                            : product.stock > 0 
                              ? 'text-yellow-600 dark:text-yellow-400'
                              : 'text-red-600 dark:text-red-400'
                        }`}>
                          {product.stock}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Stock</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Desktop Table View */}
        <div className="hidden sm:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-secondary-700 border-b border-gray-200 dark:border-secondary-600">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Product
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Stock
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-secondary-800 divide-y divide-gray-200 dark:divide-secondary-700">
              {products.map((product) => (
                <tr key={product._id} className="hover:bg-gray-50 dark:hover:bg-secondary-700 transition-colors">
                  <td className="px-6 py-4 w-1/3 max-w-0">
                    <div className="flex items-center">
                      <div className="h-10 w-10 flex-shrink-0">
                        {product.images.length > 0 ? (
                          <img
                            src={getImageUrl(product.images[0])}
                            alt={product.name}
                            className="h-10 w-10 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="h-10 w-10 rounded-lg bg-gray-200 dark:bg-secondary-700 flex items-center justify-center">
                            <Package className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                          </div>
                        )}
                      </div>
                      <div className="ml-4 min-w-0 max-w-xs sm:max-w-sm">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100 break-words leading-5 line-clamp-2">
                          {product.name}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {product.brand}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                    {product.category}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                    ₹{product.price.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                    {product.stock}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        product.active 
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400' 
                          : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400'
                      }`}>
                        {product.active ? 'Active' : 'Inactive'}
                      </span>
                      {product.featured && (
                        <Star className="h-4 w-4 text-yellow-400 fill-current" />
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <button
                      onClick={() => startEdit(product)}
                      className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 
                               p-1 rounded transition-colors"
                      title="Edit Product"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(product._id)}
                      className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 
                               p-1 rounded transition-colors"
                      title="Delete Product"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ProductManagement;